import React, { useState, useEffect } from 'react';
import { getAuthStatusDirect, logout, getGoogleAuthStatus, googleLogout, primeCsrf, clearCsrfCache, onUnauthorized } from './services/api';
import ReconcileView from './components/ReconcileView';
import RulesPanel from './components/RulesPanel';
import HealthCheckPanel from './components/HealthCheckPanel';
import BrandLockup from './components/BrandLockup';
import LoginScreen from './components/LoginScreen';
import GoogleLoginScreen from './components/GoogleLoginScreen';
import './App.css';

export default function App() {
  const [googleUser, setGoogleUser] = useState(null);  // null = loading, false = not logged in
  const [auth, setAuth] = useState(null);               // null = loading
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState('reconcile'); // 'reconcile' | 'health' | 'rules'

  // Check Google auth first, then Xero auth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');

    if (errorParam) {
      window.history.replaceState({}, '', '/');
    }

    checkGoogleAuth();
    // checkGoogleAuth is a stable function reference defined in this
    // component scope; safe to omit from deps. Run-once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to the Google login screen when any API call hits 401 —
  // session expired server-side. Without this the user stares at an
  // empty results page or stale data with no signal that they need
  // to re-authenticate.
  useEffect(() => {
    return onUnauthorized(() => {
      setGoogleUser(false);
      setAuth(null);
      setClients([]);
      setSelectedClient(null);
    });
  }, []);

  async function checkGoogleAuth() {
    try {
      const status = await getGoogleAuthStatus();
      if (status.loggedIn) {
        setGoogleUser(status.user);
        // Fetch the CSRF token up-front so the first mutating action
        // (post payment, save rule, etc.) doesn't pay a refetch penalty.
        primeCsrf();
        await checkXeroAuth();
      } else {
        setGoogleUser(false);
        setLoading(false);
      }
    } catch (err) {
      setGoogleUser(false);
      setLoading(false);
    }
  }

  async function checkXeroAuth() {
    try {
      const status = await getAuthStatusDirect();
      setAuth(status);
      if (status.authenticated && status.tenants?.length) {
        setClients(status.tenants);
        setSelectedClient(status.tenants[0]);
      }
    } catch (err) {
      setAuth({ authenticated: false });
    } finally {
      setLoading(false);
    }
  }

  async function handleFullLogout() {
    try { await logout(); } catch (e) {}
    try { await googleLogout(); } catch (e) {}
    // Invalidate the cached CSRF token so a subsequent login as a
    // different user in the same tab doesn't pay a 403→refetch cycle.
    clearCsrfCache();
    setGoogleUser(false);
    setAuth(null);
    setClients([]);
    setSelectedClient(null);
  }

  // ── Loading spinner ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // ── Step 1: Google login required ───────────────────────────
  if (!googleUser) {
    return <GoogleLoginScreen />;
  }

  // ── Step 2: Xero connection required ────────────────────────
  if (!auth?.authenticated) {
    return <LoginScreen googleUser={googleUser} onLogout={handleFullLogout} />;
  }

  // ── Step 3: Main app ─────────────────────────────────────────
  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <BrandLockup compact />
          <span className="xero-badge">for Xero</span>
        </div>

        <div className="header-center">
          {clients.length > 1 && (
            <select
              className="client-select"
              value={selectedClient?.id || ''}
              onChange={e => {
                const client = clients.find(c => c.id === e.target.value);
                setSelectedClient(client);
              }}
            >
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          {clients.length === 1 && (
            <span className="client-name">{selectedClient?.name}</span>
          )}
        </div>

        <div className="header-right">
          <nav className="header-nav">
            <button
              className={`nav-link ${page === 'reconcile' ? 'active' : ''}`}
              onClick={() => setPage('reconcile')}
            >Reconcile</button>
            <button
              className={`nav-link ${page === 'health' ? 'active' : ''}`}
              onClick={() => setPage('health')}
            >Health check</button>
            <button
              className={`nav-link ${page === 'rules' ? 'active' : ''}`}
              onClick={() => setPage('rules')}
            >Rules</button>
          </nav>
          {googleUser?.avatar && (
            <img
              src={googleUser.avatar}
              alt={googleUser.name}
              className="user-avatar"
              title={googleUser.email}
            />
          )}
          <span className="header-user">{googleUser?.name?.split(' ')[0]}</span>
          <button className="btn-ghost" onClick={handleFullLogout}>Sign out</button>
        </div>
      </header>

      <main className="app-main">
        {page === 'rules' ? (
          <RulesPanel tenantId={selectedClient?.id || null} />
        ) : page === 'health' && selectedClient ? (
          <HealthCheckPanel
            tenantId={selectedClient.id}
            clientName={selectedClient.name}
            onSelectClient={(id) => {
              const client = clients.find(c => c.id === id);
              if (client) setSelectedClient(client);
            }}
          />
        ) : selectedClient ? (
          <ReconcileView tenantId={selectedClient.id} clientName={selectedClient.name} />
        ) : (
          <div className="empty-state">
            <p>No Xero organisations found. Please check your Xero connection.</p>
          </div>
        )}
      </main>
    </div>
  );
}
